/**
 * Hook to check for upcoming virtual appointments and trigger reminders
 * Shows notification 15 minutes before appointment start time (based on clinic timezone)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { ScheduledAppointment } from '@/services/features/ScheduledAppointmentsService';
import { toZonedTime } from 'date-fns-tz';

interface UseAppointmentReminderReturn {
  upcomingAppointments: ScheduledAppointment[];
  showReminder: boolean;
  dismissReminder: () => void;
  dismissedAppointmentIds: Set<number>;
}

const REMINDER_MINUTES_BEFORE = 15;
const CHECK_INTERVAL_MS = 30000; // Check every 30 seconds

export const useAppointmentReminder = (
  virtualAppointments: ScheduledAppointment[]
): UseAppointmentReminderReturn => {
  const [upcomingAppointments, setUpcomingAppointments] = useState<ScheduledAppointment[]>([]);
  const [showReminder, setShowReminder] = useState(false);
  const [dismissedAppointmentIds, setDismissedAppointmentIds] = useState<Set<number>>(new Set());
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Check if an appointment is within the reminder window (15 minutes before start)
   * Uses the appointment's clinic timezone for accurate comparison
   */
  const isWithinReminderWindow = useCallback((appointment: ScheduledAppointment): boolean => {
    try {
      const timezone = appointment.clinic_timezone || 'UTC';
      
      // Parse the appointment start time as UTC
      const appointmentStartUTC = new Date(appointment.appointment_date_start_time);
      
      // Get current time
      const now = new Date();
      
      // Calculate difference in minutes
      const diffMs = appointmentStartUTC.getTime() - now.getTime();
      const diffMinutes = diffMs / (1000 * 60);
      
      // Check if appointment is within the reminder window:
      // - Not yet started (diffMinutes > 0)
      // - Within 15 minutes (diffMinutes <= REMINDER_MINUTES_BEFORE)
      return diffMinutes > 0 && diffMinutes <= REMINDER_MINUTES_BEFORE;
    } catch (error) {
      console.error('Error checking appointment reminder window:', error);
      return false;
    }
  }, []);

  /**
   * Check all virtual appointments and find those that need reminders
   */
  const checkForUpcomingAppointments = useCallback(() => {
    if (!virtualAppointments || virtualAppointments.length === 0) {
      setUpcomingAppointments([]);
      setShowReminder(false);
      return;
    }

    // Filter appointments that are:
    // 1. Within the 15-minute reminder window
    // 2. Not already dismissed by the user
    const appointmentsNeedingReminder = virtualAppointments.filter(
      (appointment) => 
        isWithinReminderWindow(appointment) && 
        !dismissedAppointmentIds.has(appointment.appointment_id)
    );

    if (appointmentsNeedingReminder.length > 0) {
      setUpcomingAppointments(appointmentsNeedingReminder);
      setShowReminder(true);
    } else {
      setUpcomingAppointments([]);
      // Don't auto-hide if user manually dismissed
    }
  }, [virtualAppointments, isWithinReminderWindow, dismissedAppointmentIds]);

  /**
   * Dismiss the reminder popup and track dismissed appointment IDs
   */
  const dismissReminder = useCallback(() => {
    // Add current appointments to dismissed set
    setDismissedAppointmentIds((prev) => {
      const newSet = new Set(prev);
      upcomingAppointments.forEach((apt) => newSet.add(apt.appointment_id));
      return newSet;
    });
    setShowReminder(false);
  }, [upcomingAppointments]);

  // Set up interval to check for upcoming appointments
  useEffect(() => {
    // Initial check
    checkForUpcomingAppointments();

    // Set up periodic checking
    intervalRef.current = setInterval(checkForUpcomingAppointments, CHECK_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [checkForUpcomingAppointments]);

  // Clean up dismissed appointments that are no longer in the list
  useEffect(() => {
    const currentAppointmentIds = new Set(virtualAppointments.map((a) => a.appointment_id));
    setDismissedAppointmentIds((prev) => {
      const newSet = new Set<number>();
      prev.forEach((id) => {
        if (currentAppointmentIds.has(id)) {
          newSet.add(id);
        }
      });
      return newSet;
    });
  }, [virtualAppointments]);

  return {
    upcomingAppointments,
    showReminder,
    dismissReminder,
    dismissedAppointmentIds,
  };
};
